import { useState } from "react";
import { Link } from "react-router-dom";
import { DoorOpen, Mic2, Hammer, Stethoscope, Store, Info, Bath, Armchair, LifeBuoy, Accessibility, X, Sparkles, TreePine, Utensils } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { fileUrl } from "@/lib/api";
import { timeOf } from "@/lib/time";

export const SPACE_TYPES = {
  entrada: { label: "Entrada e saída", icon: DoorOpen, color: "#1e293b" },
  palco: { label: "Palco", icon: Mic2, color: "#c2410c" },
  atividades: { label: "Espaço de atividades", icon: Sparkles, color: "#15803d" },
  restauracao: { label: "Restauração", icon: Utensils, color: "#a16207" },
  referencia: { label: "Ponto de referência", icon: TreePine, color: "#3f6212" },
  oficinas: { label: "Oficinas", icon: Hammer, color: "#b45309" },
  rastreios: { label: "Rastreios", icon: Stethoscope, color: "#b91c1c" },
  expositores: { label: "Expositores", icon: Store, color: "#7c3aed" },
  informacao: { label: "Informação", icon: Info, color: "#1d4ed8" },
  wc: { label: "Instalações sanitárias", icon: Bath, color: "#0e7490" },
  descanso: { label: "Zona de descanso", icon: Armchair, color: "#15803d" },
  apoio: { label: "Ponto de apoio", icon: LifeBuoy, color: "#be123c" },
  acesso: { label: "Acesso adaptado", icon: Accessibility, color: "#0f766e" },
};

function GardenArt() {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden>
      <rect width="100" height="100" fill="#e9f5e1" />
      <path d="M0 70 C20 60 35 78 55 68 S85 55 100 62 V100 H0Z" fill="#d6ecc8" />
      <path d="M0 20 C25 28 40 10 60 18 S90 30 100 22 V0 H0Z" fill="#dcefd0" />
      <path d="M50 100 C50 80 48 60 50 45 S52 20 50 5" stroke="#f3e6c4" strokeWidth="4" fill="none" />
      <path d="M8 40 C30 45 70 45 92 40" stroke="#f3e6c4" strokeWidth="3" fill="none" />
      <path d="M15 75 C35 72 65 72 88 76" stroke="#f3e6c4" strokeWidth="3" fill="none" />
      {[[12, 12], [88, 10], [8, 55], [94, 50], [40, 52], [62, 25], [20, 88], [75, 92], [36, 22]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={3 + (i % 3)} fill="#a7d38f" opacity=".7" />
      ))}
      <ellipse cx="50" cy="56" rx="7" ry="4" fill="#bfe3f2" />
    </svg>
  );
}

export default function VenueMap({ initial, compact = false }) {
  const site = useSite();
  const spaces = site.data.spaces;
  const mapImage = site.data.settings?.map_image;
  const [sel, setSel] = useState(() => spaces.find((s) => s.slug === initial) || null);
  const [type, setType] = useState("");
  const visible = spaces.filter((s) => !type || s.type === type);
  const acts = sel ? site.data.activities.filter((a) => a.space_id === sel.id) : [];
  const orgsHere = sel ? site.data.organizations.filter((o) => o.space_id === sel.id && o.stand).sort((a, b) => a.stand - b.stand) : [];
  const types = Object.entries(SPACE_TYPES).filter(([k]) => spaces.some((s) => s.type === k));

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-testid="venue-map">
      <div className="min-w-0">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3" role="toolbar" aria-label="Filtrar pontos do mapa">
          <button onClick={() => setType("")} aria-pressed={!type} className={`shrink-0 h-10 px-4 rounded-full text-sm font-semibold border ${!type ? "bg-[var(--ink)] text-white" : "bg-white"}`} data-testid="map-filter-all">Todos</button>
          {types.map(([k, t]) => (
            <button key={k} onClick={() => setType(k)} aria-pressed={type === k} data-testid={`map-filter-${k}`}
              className={`shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border ${type === k ? "text-white" : "bg-white"}`} style={type === k ? { background: t.color, borderColor: t.color } : {}}>
              <t.icon className="h-4 w-4" aria-hidden />{t.label}
            </button>
          ))}
        </div>
        <div className={mapImage ? "overflow-x-auto rounded-3xl border border-emerald-900/10 shadow-sm bg-white" : ""} data-testid="map-scroll">
        <div className={mapImage ? "relative min-w-[720px]" : `relative w-full overflow-hidden rounded-3xl border border-emerald-900/10 shadow-sm ${compact ? "aspect-[4/3] sm:aspect-[16/9]" : "aspect-[3/4] sm:aspect-[16/10]"}`}>
          {mapImage ? <img src={fileUrl(mapImage)} alt="Planta oficial do recinto do Jardim Botânico da Ajuda, com zonas identificadas por letras e bancas numeradas" className="block w-full h-auto" /> : <GardenArt />}
          {!mapImage && <p className="absolute left-3 bottom-3 right-3 sm:right-auto bg-white/90 rounded-xl px-3 py-2 text-xs font-semibold text-amber-900" data-testid="map-demo-notice">Marcadores demonstrativos — a aguardar a planta oficial do evento.</p>}
          {visible.map((s) => {
            const t = SPACE_TYPES[s.type] || SPACE_TYPES.informacao;
            const active = sel?.id === s.id;
            return (
              <button key={s.id} onClick={() => setSel(s)} aria-label={s.name} aria-pressed={active} data-testid={`map-marker-${s.slug}`}
                className={`absolute -translate-x-1/2 -translate-y-1/2 grid place-items-center rounded-full text-white shadow-lg ring-4 transition-transform hover:scale-110 ${active ? "h-12 w-12 ring-[var(--sun)] scale-110 z-10" : mapImage ? "h-9 w-9 ring-white" : "h-11 w-11 ring-white/70"}`}
                style={{ left: `${s.x}%`, top: `${s.y}%`, background: t.color }}>
                {s.code ? <span className="font-display font-black text-xs" aria-hidden>{s.code}</span> : <t.icon className="h-5 w-5" aria-hidden />}
              </button>
            );
          })}
        </div>
        </div>
        {mapImage && <p className="mt-2 text-xs text-slate-600 sm:hidden">Deslize o mapa para os lados para ver todo o recinto.</p>}
      </div>
      <aside className="bg-white rounded-3xl border border-black/5 p-6 lg:self-start lg:sticky lg:top-24" aria-live="polite" data-testid="map-detail-panel">
        {sel ? (
          <>
            <div className="flex items-start justify-between gap-2">
              <p className="eyebrow" style={{ color: SPACE_TYPES[sel.type]?.color }}>{SPACE_TYPES[sel.type]?.label}</p>
              <button onClick={() => setSel(null)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Fechar" data-testid="map-detail-close"><X className="h-4 w-4" /></button>
            </div>
            <h3 className="font-display text-2xl font-bold mt-1" data-testid="map-detail-title">{sel.code && sel.code !== "WC" ? `${sel.code} · ` : ""}{sel.name}</h3>
            <p className="text-slate-700 mt-2 text-sm">{sel.description}</p>
            {acts.length > 0 && (
              <div className="mt-5">
                <p className="eyebrow text-slate-500 mb-2">Atividades neste espaço</p>
                <ul className="space-y-1">
                  {acts.slice(0, compact ? 4 : 20).map((a) => (
                    <li key={a.id}><Link to={`/atividade/${a.slug}`} className="flex gap-3 py-2 border-b border-black/5 hover:text-[var(--leaf)]" data-testid={`map-activity-${a.slug}`}><span className="font-mono-x text-sm w-12 shrink-0">{timeOf(a.start)}</span><span className="text-sm font-medium">{a.title}</span></Link></li>
                  ))}
                </ul>
              </div>
            )}
            {orgsHere.length > 0 && (
              <div className="mt-5">
                <p className="eyebrow text-slate-500 mb-2">Bancas neste espaço</p>
                <ul className="max-h-72 overflow-y-auto">
                  {orgsHere.map((o) => <li key={o.id}><Link to={`/organizacoes/${o.slug}`} className="flex gap-3 py-2 border-b border-black/5 hover:text-[var(--leaf)]" data-testid={`map-org-${o.slug}`}><span className="font-mono-x text-sm w-8 shrink-0">{o.stand}</span><span className="text-sm font-medium">{o.name}</span></Link></li>)}
                </ul>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="font-display text-xl font-bold">Explore o recinto</p>
            <p className="text-sm text-slate-700 mt-2">Toque num marcador para ver o espaço e as atividades que lá acontecem.</p>
            <ul className="mt-5 grid grid-cols-1 gap-2 text-sm">
              {types.map(([k, t]) => <li key={k} className="flex items-center gap-2"><span className="h-6 w-6 rounded-full grid place-items-center text-white" style={{ background: t.color }}><t.icon className="h-3.5 w-3.5" aria-hidden /></span>{t.label}</li>)}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}
